import i18n from '../../../i18n';
import { fmtInt, fmtMoney, fmtNumber, fmtRate, fmtShare } from '../lib/growthFormat';
import type {
  GrowthChannel,
  GrowthMetric,
  GrowthMetricUnit,
  GrowthPayload,
  GrowthRestaurant,
  GrowthRestaurantSourceState,
  GrowthSource,
  GrowthSourceCoverage,
  GrowthSourceStatus,
  GrowthSubview,
  GrowthUnavailableReason,
} from './growthTypes';
import { GROWTH_ANALYTIC_SUBVIEWS, GROWTH_CONNECTED_SUBVIEWS, GROWTH_SOURCE_TO_SETUP } from './growthTypes';
// The paid lane is Google's reach only: no reservation step (contract 42).
export const PAID_STAGE_IDS = ['impression', 'click', 'session'] as const;

/**
 * The Growth sections a subview asks for (WP2.8): Sources the X-ray and the
 * Google reports its layers need (concept section 14), Overview Analytics
 * totals, Guests no Google report, Website and reservation the Analytics
 * tables (WP5.2), Local and search Business Profile, Search Console and Ads
 * reach (WP5.1), Data health each product's core report (WP4.3). Campaigns
 * and setup read their own methods: they ask for the cheapest section, which
 * still carries every source's status and the venues.
 */
export function growthSectionsFor(subview: string): string[] {
  if (
    subview === 'sources' ||
    subview === 'overview' ||
    subview === 'website' ||
    subview === 'guests' ||
    subview === 'local' ||
    subview === 'health'
  ) {
    return [subview];
  }
  // Old links (Channels) read as Sources.
  if (subview === 'channels') return ['sources'];
  return ['guests'];
}

/** A Growth section the sidebar lists under Growth. */
export type GrowthNavSection = Exclude<GrowthSubview, 'setup' | 'accounts'>;

/**
 * The sections the sidebar lists under Growth, from the connection status of
 * the venues in view (whether a Google product feeding Website or Local and
 * search is connected), never from a section's payload while it loads. The
 * demo group has every section. Unknown status leaves the two connected
 * sections out; the caller keeps the last known state while it loads.
 */
export function growthNavSections(
  availability: Pick<GrowthPayload, 'sectionAvailability'>['sectionAvailability'] | null | undefined,
  demo = false,
): GrowthNavSection[] {
  if (demo) return [...GROWTH_ANALYTIC_SUBVIEWS];
  return GROWTH_ANALYTIC_SUBVIEWS.filter(
    id => !GROWTH_CONNECTED_SUBVIEWS.has(id) || availability?.[id]?.available === true,
  );
}

/** Sections hidden because their Google source is not connected, with the products any one of which would add them. */
export function hiddenGrowthSections(
  payload: Pick<GrowthPayload, 'sectionAvailability'> | null | undefined,
): { id: 'website' | 'local'; needs: string[] }[] {
  const availability = payload?.sectionAvailability;
  if (!availability) return [];
  return (['website', 'local'] as const)
    .filter(id => availability[id]?.available === false)
    .map(id => ({ id, needs: availability[id]?.needs ?? [] }));
}

/** The view context's statuses, sources and shifts Growth applies (decision 7.5.2). */
export type GrowthFilterChoice = { status: string[]; source: string[]; shifts: string[] };

export function hasGrowthFilters(filters: GrowthFilterChoice | null | undefined): filters is GrowthFilterChoice {
  return Boolean(filters && (filters.status.length || filters.source.length || filters.shifts.length));
}

/** The filters as a Sources request names them (as getGrowth takes them). */
export function growthFilterRequest(
  filters: GrowthFilterChoice | null | undefined,
): { status?: string[]; source?: string[]; shift?: string[] } {
  if (!hasGrowthFilters(filters)) return {};
  return {
    ...(filters.status.length ? { status: filters.status } : {}),
    ...(filters.source.length ? { source: filters.source } : {}),
    ...(filters.shifts.length ? { shift: filters.shifts } : {}),
  };
}

/**
 * Positional arguments of opsCommand/getGrowth: restaurant, period settings,
 * the view context's venue filter (decision 7.7.1: Growth reads the venues
 * on screen; null is the restaurant opened), five legacy slots, sections,
 * refresh, then the view context's status, source and shift filters
 * (decision 7.5.2), only when one is set.
 */
export function growthMethodArgs(
  restaurantId: string,
  settings: Record<string, unknown>,
  sections: string[],
  refresh = false,
  regionFilter: string | null = null,
  filters: GrowthFilterChoice | null = null,
): unknown[] {
  const venues = regionFilter && regionFilter !== 'all' ? regionFilter : null;
  const args: unknown[] = [restaurantId, settings, venues, null, null, null, null, null, sections];
  if (hasGrowthFilters(filters)) {
    const list = (values: string[]) => (values.length ? values : null);
    args.push(refresh, list(filters.status), list(filters.source), list(filters.shifts));
  } else if (refresh) {
    args.push(true);
  }
  return args;
}

/** Whether the status filter leaves an outcome out: shown as excluded, never as zero. */
export function outcomeExcluded(
  view: Pick<GrowthPayload, 'filters'> | null | undefined,
  outcome: 'completed' | 'cancelled' | 'noShow',
): boolean {
  return view?.filters?.outcomes?.[outcome] === 'excluded';
}

export type GrowthCoverageSourceId = 'all' | 'ga4' | 'ads' | 'gsc' | 'gbp' | 'pos';

export function sourceCoverageFor(
  payload: GrowthPayload | null | undefined,
  sourceId: string,
): GrowthSourceCoverage | undefined {
  return payload?.sourceCoverage?.find(source => source.id === sourceId);
}

export function restaurantSourceStatus(
  restaurant: GrowthRestaurant,
  sourceId: string,
): string {
  const state: GrowthRestaurantSourceState | undefined = restaurant.sourceStates?.[sourceId];
  if (typeof state === 'string') return state;
  return state?.status || 'not_connected';
}

export function restaurantMetric(
  restaurant: GrowthRestaurant,
  key: keyof NonNullable<GrowthRestaurant['metrics']>,
): GrowthMetric | null {
  const value = restaurant.metrics?.[key];
  if (typeof value === 'number') {
    return {
      value,
      provenance: 'observed',
      available: true,
    };
  }
  if (!value) return null;
  return {
    ...value,
    value: value.value ?? null,
    provenance: value.provenance ?? 'observed',
  };
}

export function metricAvailable(metric?: GrowthMetric | null): boolean {
  if (!metric) return false;
  if (metric.available === false) return false;
  return metric.value != null;
}

export function formatMetric(metric?: GrowthMetric | null, digits = 0): string {
  if (!metricAvailable(metric) || metric?.value == null) return '—';
  const n = metric.value;
  const unit: GrowthMetricUnit | undefined = metric.unit;
  if (unit === 'ratio') return fmtShare(n, 1);
  if (unit === 'currency' || (metric.currency && Math.abs(n) >= 1 && unit !== 'count')) {
    if (metric.currency) return fmtMoney(n, metric.currency);
  }
  if (unit !== 'count' && unit !== 'reservations' && unit !== 'covers' && Math.abs(n) < 1 && n !== 0) {
    return fmtShare(n, 1);
  }
  return digits ? fmtNumber(n, digits) : fmtInt(n);
}

export function formatRate(metric?: GrowthMetric | null): string {
  if (!metricAvailable(metric) || metric?.value == null) return '—';
  return fmtShare(metric.value, 1);
}

/**
 * A conversion rate as a percentage, keeping a small one legible.
 *
 * Site rates run from a fraction of a percent to a few percent, and one
 * decimal turns 0.04% into "0.0%" — a real number rendered as nothing.
 */
export function formatSiteRate(value: number): string {
  return fmtRate(value);
}

export function unavailableLabel(reason?: GrowthUnavailableReason | string | null): string {
  switch (reason) {
    case 'not_connected':
      return i18n.t('growth:unavailable.not_connected');
    case 'needs_mapping':
      return i18n.t('growth:unavailable.needs_mapping');
    case 'needs_reauth':
      return i18n.t('growth:unavailable.needs_reauth');
    case 'syncing':
      return i18n.t('growth:unavailable.syncing');
    case 'no_data':
      return i18n.t('growth:unavailable.no_data');
    case 'error':
      return i18n.t('growth:unavailable.error');
    case 'product_not_granted':
    case 'access_removed':
    case 'account_inactive':
    case 'api_not_enabled':
    case 'rate_limited':
    // WP3.7: a failed read, the connection list unreadable, a campaign key
    // already counted under another campaign.
    case 'source_error':
    case 'connections_unavailable':
    case 'shared_key':
      return i18n.t(`growth:unavailable.${reason}`);
    case 'quality_blocked':
      return i18n.t('growth:unavailable.quality_blocked');
    case 'context_only':
      return i18n.t('growth:unavailable.context_only');
    case 'no_exact_match':
      return i18n.t('growth:unavailable.no_exact_match');
    case 'mixed_currency':
      return i18n.t('growth:unavailable.mixed_currency');
    case 'pos_unavailable':
      return i18n.t('growth:unavailable.pos_unavailable');
    case 'no_coverage':
      return i18n.t('growth:unavailable.no_coverage');
    case 'no_denominator':
      return i18n.t('growth:unavailable.no_denominator');
    case 'no_conversion_signal':
      return i18n.t('growth:unavailable.no_conversion_signal');
    case 'offline':
    case 'timeout':
    case 'incomplete':
    case 'history_limit':
    case 'not_requested':
    case 'no_brand_terms':
      return i18n.t(`growth:unavailable.${reason}`);
    default:
      return i18n.t('growth:unavailable.default');
  }
}

export function formatCount(value: number | null | undefined): string {
  return fmtInt(value);
}

export function findChannel(
  payload: GrowthPayload | null | undefined,
  id: string | null,
): GrowthChannel | null {
  if (!id) return null;
  return (payload?.channels || []).find(c => c.id === id) ?? null;
}

export function sourceStatusLabel(status: GrowthSourceStatus): string {
  switch (status) {
    case 'ready':
      return i18n.t('growth:sourceStatus.ready');
    case 'not_connected':
      return i18n.t('growth:sourceStatus.not_connected');
    case 'needs_mapping':
      return i18n.t('growth:sourceStatus.needs_mapping');
    case 'needs_reauth':
      return i18n.t('growth:sourceStatus.needs_reauth');
    case 'syncing':
      return i18n.t('growth:sourceStatus.syncing');
    case 'no_data':
      return i18n.t('growth:sourceStatus.no_data');
    case 'error':
      return i18n.t('growth:sourceStatus.error');
    case 'product_not_granted':
    case 'access_removed':
    case 'account_inactive':
    case 'api_not_enabled':
    case 'rate_limited':
    case 'offline':
    case 'timeout':
    case 'incomplete':
    case 'history_limit':
    case 'not_requested':
    case 'connections_unavailable':
      return i18n.t(`growth:sourceStatus.${status}`);
    default:
      return status;
  }
}

/**
 * Statuses that need someone to act: a new Google sign-in, a failed refresh,
 * or a connection problem (WP1.10). Rate limits retry on their own.
 */
export const SOURCE_ATTENTION_STATUSES: readonly GrowthSourceStatus[] = [
  'needs_reauth',
  'product_not_granted',
  'access_removed',
  'account_inactive',
  'api_not_enabled',
  'error',
  // WP3.7: the connection list could not be read; not "not connected".
  'connections_unavailable',
];

/** Google sources (not POS) that need attention, in payload order. */
export function googleSourcesNeedingAttention(
  payload: GrowthPayload | null | undefined,
): GrowthSource[] {
  return (payload?.sources || []).filter(
    source => source.id !== 'pos' && SOURCE_ATTENTION_STATUSES.includes(source.status),
  );
}

/**
 * Where the venues in view stand on their data sources, for the one status
 * the scope bar shows (it links to Data health).
 *
 * - `ready`: every Google product reads and every venue in view links POS.
 * - `attention`: a connection needs someone to act.
 * - `partial`: something is connected, not everything.
 * - `none`: nothing is connected: no Google product, no POS-linked venue.
 */
export type GrowthSourcesSummary = {
  google: { connected: number; ready: number; total: number };
  /**
   * POS: how many venues in view link POS revenue. `connected` is false only
   * when the server says POS is not connected anywhere in the org.
   */
  pos: { linked: number; venues: number; connected: boolean };
  tone: 'ready' | 'attention' | 'partial' | 'none';
};

export function growthSourcesSummary(
  payload: GrowthPayload | null | undefined,
  ids: string[],
): GrowthSourcesSummary {
  const sources = payload?.sources || [];
  const google = sources.filter(source => source.id !== 'pos');
  const connected = google.filter(
    source => source.status !== 'not_connected' && source.status !== 'connections_unavailable',
  ).length;
  const ready = google.filter(source => source.status === 'ready').length;
  const total = google.length || 4;
  const pos = sources.find(source => source.id === 'pos');
  const inView = new Set(ids);
  const covered = sourceCoverageFor(payload, 'pos')?.coveredRestaurantIds ?? [];
  const linked = covered.filter(id => inView.has(id)).length;
  const posConnected = Boolean(pos) && pos?.status !== 'not_connected';
  const venues = ids.length;
  const tone: GrowthSourcesSummary['tone'] =
    googleSourcesNeedingAttention(payload).length > 0
      ? 'attention'
      : ready === total && google.length > 0 && venues > 0 && linked === venues
        ? 'ready'
        : connected === 0 && linked === 0
          ? 'none'
          : 'partial';
  return {
    google: { connected, ready, total },
    pos: { linked, venues, connected: posConnected },
    tone,
  };
}

export function setupKindForSource(sourceId: string): 'ga' | 'googleads' | 'gsc' | 'gbp' | null {
  return GROWTH_SOURCE_TO_SETUP[sourceId] ?? null;
}

export function primarySetupAction(payload: GrowthPayload | null | undefined): {
  sourceId: string;
  label: string;
  kind: 'ga' | 'googleads' | 'gsc' | 'gbp';
} | null {
  const sources = payload?.sources || [];
  const rank: GrowthSourceStatus[] = [
    'error',
    'needs_reauth',
    'product_not_granted',
    'access_removed',
    'account_inactive',
    'needs_mapping',
    'not_connected',
    'no_data',
    'syncing',
  ];
  for (const status of rank) {
    const hit = sources.find(s => s.id !== 'pos' && s.status === status);
    if (!hit) continue;
    const kind = setupKindForSource(hit.id);
    if (!kind) continue;
    return {
      sourceId: hit.id,
      kind,
      label: hit.action?.label || sourceStatusLabel(hit.status),
    };
  }
  return null;
}

export function sourcesOrFallback(payload: GrowthPayload | null | undefined): GrowthSource[] {
  if (payload?.sources?.length) return payload.sources;
  if (!payload) return [];

  const normalize = (id: string) =>
    id === 'ga' ? 'ga4' : id === 'googleads' ? 'ads' : id;
  const connected = new Set((payload.sourcesConnected || []).map(normalize));
  for (const resource of payload.resources || []) connected.add(normalize(resource.kind));
  if (metricAvailable(payload.revenue?.linkedPos)) connected.add('pos');

  const definitions = [
    ['ga4', i18n.t('growth:fallbackSources.ga4')],
    ['ads', i18n.t('growth:fallbackSources.ads')],
    ['gsc', i18n.t('growth:fallbackSources.gsc')],
    ['gbp', i18n.t('growth:fallbackSources.gbp')],
    ['pos', i18n.t('growth:fallbackSources.pos')],
  ] as const;

  return definitions.map(([id, label]) => {
    const resources = (payload.resources || []).filter(
      resource => normalize(resource.kind) === id,
    );
    const needsReauth = resources.some(resource => resource.syncStatus === 'reauth_required');
    const syncing = resources.some(resource =>
      ['queued', 'syncing', 'running'].includes(resource.syncStatus || ''),
    );
    const errored = resources.some(resource => resource.syncStatus === 'error');
    const status: GrowthSourceStatus = needsReauth
      ? 'needs_reauth'
      : errored
        ? 'error'
        : syncing
          ? 'syncing'
          : connected.has(id)
            ? 'ready'
            : 'not_connected';
    return {
      id,
      label,
      status,
      action:
        id === 'pos'
          ? { id: 'none', label: null }
          : status === 'ready'
            ? { id: 'manage', label: i18n.t('growth:setup.manage') }
            : status === 'needs_reauth'
              ? { id: 'reconnect', label: i18n.t('growth:setup.reconnect') }
              : { id: 'connect', label: i18n.t('growth:setup.connect') },
    };
  });
}
